/**
 * Scene Graph — The structured representation of a cinematic scene.
 * Every field is nullable: null = user did NOT mention it.
 * Inferred values are tracked separately so we never override user intent.
 */

// ─── Node Types ───

export interface CharacterNode {
  type: string | null;         // zombie, warrior, girl, robot …
  emotion: string | null;      // laughing, crying, angry …
  clothing: string | null;     // suit, armor, dress …
  pose: string | null;         // standing, sitting, walking …
  skinDetail: string | null;   // detailed undead skin, smooth …
  action: string | null;       // running, fighting, dancing …
  gender: string | null;
  age: string | null;
}

export interface CameraNode {
  distance: 'close' | 'medium' | 'far' | null;
  lens: string | null;         // 24mm, 35mm, 50mm, 85mm, 135mm
  angle: string | null;        // eye_level, low_angle, high_angle, birds_eye
  shotType: string | null;     // close_up, medium, wide, extreme_wide
  movement: string | null;     // static, dolly, crane, handheld
  dof: string | null;          // shallow, moderate, deep
}

export interface LightingNode {
  type: string | null;         // natural, dramatic, horror, soft …
  intensity: string | null;    // low_key, high_key, balanced
  direction: string | null;    // front, side, back, rim
  colorTemp: string | null;    // warm, cool, neutral
  preset: string | null;       // golden_hour, blue_hour, none
}

export interface EnvironmentNode {
  location: string | null;     // forest, desert, temple, city …
  time: string | null;         // morning, golden_hour, night …
  weather: string | null;      // clear, rain, snow, fog …
  atmosphere: string | null;   // foggy, dusty, misty, clean …
}

export interface StyleNode {
  genre: string | null;        // horror, epic, romantic, sci-fi …
  renderStyle: string | null;  // photorealistic, cinematic, anime …
  colorPalette: string | null; // desaturated, warm_tones, teal_orange …
  mood: string | null;         // eerie, dramatic, peaceful, tense …
}

// ─── Scene Graph ───

export interface SceneGraph {
  /** Layer 1 — User's raw prompt, NEVER modified */
  rawPrompt: string;

  /** Layer 2 — Parsed + inferred scene data */
  character: CharacterNode;
  camera: CameraNode;
  lighting: LightingNode;
  environment: EnvironmentNode;
  style: StyleNode;

  /** Tracking metadata */
  meta: {
    /** Fields the user explicitly specified */
    userSpecifiedFields: string[];
    /** Fields the system inferred */
    inferredFields: string[];
    /** Overall parsing confidence 0-1 */
    confidence: number;
  };
}

// ─── Factory ───

export const createEmptyCharacter = (): CharacterNode => ({
  type: null, emotion: null, clothing: null, pose: null,
  skinDetail: null, action: null, gender: null, age: null,
});

export const createEmptyCamera = (): CameraNode => ({
  distance: null, lens: null, angle: null, shotType: null,
  movement: null, dof: null,
});

export const createEmptyLighting = (): LightingNode => ({
  type: null, intensity: null, direction: null, colorTemp: null, preset: null,
});

export const createEmptyEnvironment = (): EnvironmentNode => ({
  location: null, time: null, weather: null, atmosphere: null,
});

export const createEmptyStyle = (): StyleNode => ({
  genre: null, renderStyle: null, colorPalette: null, mood: null,
});

export const createEmptySceneGraph = (rawPrompt = ''): SceneGraph => ({
  rawPrompt,
  character: createEmptyCharacter(),
  camera: createEmptyCamera(),
  lighting: createEmptyLighting(),
  environment: createEmptyEnvironment(),
  style: createEmptyStyle(),
  meta: { userSpecifiedFields: [], inferredFields: [], confidence: 0 },
});

// ─── Merge Utility (for incremental edits) ───

/** Merge newGraph into existing, keeping existing user values unless overridden */
export function mergeSceneGraphs(existing: SceneGraph, patch: Partial<SceneGraph>): SceneGraph {
  const merged = { ...existing };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mergeNode = (base: any, update: any): any => {
    if (!update) return base;
    const result = { ...base };
    for (const key of Object.keys(update)) {
      const val = update[key];
      if (val !== null && val !== undefined) {
        result[key] = val;
      }
    }
    return result;
  };

  if (patch.character) merged.character = mergeNode(existing.character, patch.character);
  if (patch.camera) merged.camera = mergeNode(existing.camera, patch.camera);
  if (patch.lighting) merged.lighting = mergeNode(existing.lighting, patch.lighting);
  if (patch.environment) merged.environment = mergeNode(existing.environment, patch.environment);
  if (patch.style) merged.style = mergeNode(existing.style, patch.style);

  if (patch.meta) {
    merged.meta = {
      userSpecifiedFields: [...new Set([...existing.meta.userSpecifiedFields, ...(patch.meta.userSpecifiedFields || [])])],
      inferredFields: [...new Set([...existing.meta.inferredFields, ...(patch.meta.inferredFields || [])])],
      confidence: patch.meta.confidence ?? existing.meta.confidence,
    };
  }

  return merged;
}

/** Check if a field was user-specified (should not be overridden by inference) */
export function isUserSpecified(graph: SceneGraph, fieldPath: string): boolean {
  return graph.meta.userSpecifiedFields.includes(fieldPath);
}
